<template>
  <div style="height: 100%">
    <ag-grid-vue
      style="height: 58em"
      class="ag-theme-material"
      id="myGrid"
      :gridOptions="gridOptions"
      :columnDefs="columnDefs"
      :defaultColDef="defaultColDef"
      :overlayLoadingTemplate="overlayLoadingTemplate"
      :overlayNoRowsTemplate="overlayNoRowsTemplate"
      :suppressRowHoverHighlight="true"
      enableCellTextSelection="true"
      suppressScrollOnNewData="true"
      ref="resultsGrid"
    />
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue3";
import moment from "moment";
import DocumentNameColumn from "./DocumentNameColumn.vue";
import FileInfoButtons from "./FileInfoButtons.vue";
import HiddenColumnCheckbox from "./HiddenColumnCheckbox.vue";
import { computed, onBeforeMount, ref, shallowRef, watch } from "vue";
import CommonHelper from "../helper/CommonHelper.js";
import NotificationHelper from "../helper/NotificationHelper";
import { useQuasar } from "quasar";

export default {
  props: {
    sideBarMode: String,
    scopeFields: Array,
    userEditAccessLevel: Boolean,
    userDeleteAccessLevel: Boolean,
    foldersTable: Object,
    resultTree: Object,
    filePreview: Object,
    fileDelete: Object,
    fileVersionAdmin: Object
  },
  components: {
    AgGridVue
  },
  emits: ["editMetadata", "setSideBarMode"],
  setup (props, {emit}) {
    const resultsGrid = shallowRef();
    const gridOptions = ref(null);
    let fileInfoColumnWidth;

    onBeforeMount(() => {
      gridOptions.value = {
        pagination: true,
        paginationPageSize: 50,
        rowStyle: {
          border: "none",
        },
        getRowId: (params) => params.data.id
      };
      fileInfoColumnWidth = 52;
    });

    const overlayLoadingTemplate = ref(`
                                  <div class="spinner-table">
                                    <div class="loading-text" style="line-height: 22px">
                                      <img src="fd-logo.svg">
                                    </div>
                                    <div class="spinner-item"></div>
                                    <div class="spinner-item"></div>
                                  </div> 
                                  <span class="ag-overlay-loading-center " style="font-size: 18px; z-index: 100000"> Loading Rows ...</span>`);

    const overlayNoRowsTemplate = ref(
      `<span class="ag-overlay-loading-center" style="font-size: 16px">There are no rows to show</span>`
    );

    watch(CommonHelper.rowData, () => {
      resultsGrid.value.api.setGridOption('rowData', CommonHelper.rowData.value);
      showHideOverlays();
      reSize();
    });

    watch(CommonHelper.loadingInitialSearchResults, () => {
      if (!CommonHelper.loadingInitialSearchResults.value) {
        if (CommonHelper.searchSubmitted.value && CommonHelper.rowData.value.length === 0 && !CommonHelper.searchCanceled.value) {
          if (props.sideBarMode === "advancedSearch" || props.sideBarMode === "advancedSearchHidden" || props.sideBarMode === "singularSearch" || props.sideBarMode === "singularSearchHidden") {
            showNoResultsNotification();

            if (CommonHelper.searchError.value) {
              NotificationHelper.createErrorNotification("Error encountered. Please try again.");
            }
          }
        }
      }
    });

    const $q = useQuasar();
    function showNoResultsNotification() {
      $q.notify({
        color: "primary",
        textColor: "white",
        icon: null,
        message:
          "<strong>Sorry, no results found! Please try to change your keywords</strong>",
        position: "center",
        avatar: null,
        multiLine: true,
        actions: [
          {
            label: "Dismiss",
            color: "white",
            handler: () => {
            },
          },
        ],
        timeout: Math.random() * 5000 + 3000,
        html: true,
      });
    }

    const truncatedScopeFields = ["action_category", "company_name", "date", "file_type", "file_year", "island", "job_number", "label", "location_id",
      "report_date", "scope", "service_contract_type"];

    const visibleScopeFields = computed(() => {
      let returnValue = [];

      CommonHelper.rowData.value.forEach((row) => {
        if (returnValue.length !== props.scopeFields.length) {
          props.scopeFields.forEach((scopeField) => {
            if (!returnValue.includes(scopeField) && row[scopeField] !== null && row[scopeField] !== "") {
              returnValue.push(scopeField);
            }
          })
        }
      })

      if (!CommonHelper.displayHiddenColumns.value && returnValue.length > 10) {
        const truncatedReturnValue = returnValue.filter((scopeField) => {
          return truncatedScopeFields.includes(scopeField);
        });

        if (truncatedReturnValue.length > 0) {
          returnValue = truncatedReturnValue;
        }
      }

      return returnValue.length > 0 ? returnValue : props.scopeFields;
    });

    watch(visibleScopeFields, () => {
      setColumnVisible();
    });

    /*
     * Sets the columnVisible values for the search results grid
     */
    function setColumnVisible() {
      const scopeFieldsToShow = props.scopeFields.filter((scopeField) => {
        return visibleScopeFields.value.includes(scopeField);
      })

      const scopeFieldsToHide = props.scopeFields.filter((scopeField) => {
        return !visibleScopeFields.value.includes(scopeField);
      })

      resultsGrid.value.api.setColumnsVisible(scopeFieldsToShow, true);
      resultsGrid.value.api.setColumnsVisible(scopeFieldsToHide, false);
    }

    const defaultColDef = ref({
      autoHeaderHeight: true,
      editable: false,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      sortable: true,
      suppressHeaderMenuButton: true
    });

    const columnDefs = computed(() => {
      var columnDefsArray = [];

      columnDefsArray.push(getFileInfoColumnDef());
      columnDefsArray.push(getDocumentNameColumnDef());

      props.scopeFields.forEach((scopeField) => {
        if (scopeField !== "label") {
          columnDefsArray.push(getColumnDef(scopeField));
        }
      });

      return columnDefsArray;
    });

    watch(columnDefs, () => {
      // reset columnDefs so that the order will match props.scopeFields
      resultsGrid.value.api.setGridOption('columnDefs', []);

      setTimeout(() => {
        setColumnVisible();
      }, 100);
    });

    /*
     * Returns a columnDef from the given parameters
     */
    function getColumnDef(scopeField) {
      var headerName = "";
      var cellStyle = {};
      var comparator = null;
      var valueFormatter = function (params) {
        return params.value;
      };

      switch (scopeField) {
        case "action_category":
          headerName = "Category";
          break;
        case "company_name":
          headerName = "Company Name";
          break;
        case "date":
          headerName = "Date Created";
          comparator = dateComparator;
          break;
        case "delivery_party":
          headerName = "Delivery Party";
          break;
        case "description":
          headerName = "Description";
          break;
        case "document_category":
          headerName = "Document Category";
          break;
        case "file_type":
          headerName = "Type";
          break;
        case "file_year":
          headerName = "Year";
          break;
        case "invoice_number":
          headerName = "Invoice Number";
          break;
        case "island":
          headerName = "Island";
          break;
        case "job_number":
          headerName = "Job Number";
          break;
        case "location":
          headerName = "Location";
          break;
        case "location_id":
          headerName = "School Name";
          break;
        case "project_number":
          headerName = "Project Number";
          break;
        case "purchase_order_number":
          headerName = "Purchase Order Number";
          break;
        case "purchase_order_originator":
          headerName = "Purchase Order Originator";
          break;
        case "report_date":
          headerName = "Report Date";
          comparator = dateComparator;
          break;
        case "scope":
          headerName = "Scope";
          break;
        case "service_contract_type":
          headerName = "Service Contract Type";
          break;
        case "vendor_or_supplier":
          headerName = "Vendor or Supplier";
          break;
        case "work_order_number":
          headerName = "Work Order Number";
          break;
      }

      var columnDef =
        {
          headerName: headerName,
          headerTooltip: headerName,
          field: scopeField,
          flex: 1,
          tooltipField: scopeField,
          cellStyle: cellStyle,
          comparator: comparator,
          autoHeight: true,
          lockPosition: "left",
          valueFormatter: valueFormatter
        };

      return columnDef;
    }

    /*
     * Returns the file_info columnDef
     */
    function getFileInfoColumnDef() {
      var valueFormatter = function (params) {
        return params.value;
      };
      var columnDef =
        {
          headerName: "",
          field: "file_info",
          lockPosition: "left",
          headerComponent : HiddenColumnCheckbox,
          headerClass: "hidden-column-checkbox",
          cellRenderer: FileInfoButtons,
          cellRendererParams: {
            userEditAccessLevel: props.userEditAccessLevel,
            userDeleteAccessLevel: props.userDeleteAccessLevel,

            displayFolder: function(newValues) {
              props.foldersTable.setCurrentValues(newValues);
              emit("setSideBarMode", "folders");
            },

            displayTreeModal: function(documentId, treeTitle) {
              props.resultTree.displayTreeModal(documentId, treeTitle);
            },

            downloadFile: function(documentId, originalFilename) {
              props.filePreview.downloadFile(documentId, originalFilename, true);
            },

            editMetadata: function(documentId) {
              for (let i = 0; i < resultsGrid.value.api.getDisplayedRowCount(); i++) {
                if (resultsGrid.value.api.getDisplayedRowAtIndex(i).data.file_info[0] === documentId) {
                  emit("editMetadata", resultsGrid.value.api.getDisplayedRowAtIndex(i).data);
                  break;
                }
              }
            },

            deleteFile: function (documentId, fileName, scopeName) {
              props.fileDelete.deleteRow(documentId, fileName, scopeName);
            }
          },
          minWidth: fileInfoColumnWidth,
          maxWidth: fileInfoColumnWidth,
          width: fileInfoColumnWidth,
          autoHeight: true,
          floatingFilter: false,
          resizable: false,
          sortable: false,
          filter: null,
          cellClass: 'ag-right-aligned-cell',
          cellStyle: {
            backgroundColor: "#0067C1",
            color: "#FFFFFF",
            padding: "0px 0px 0px 15px",
          },
          valueFormatter: valueFormatter,
        };

      return columnDef;
    }

    /*
     * Returns the document_name columnDef
     */
    function getDocumentNameColumnDef() {
      let valueFormatter = function (params) {
        return params.value;
      };
      let columnDef =
        {
          headerName: "Document Name",
          headerTooltip: "Document Name",
          field: "document_name",
          flex: 1,
          cellStyle: {
            "background-color": "#0067C1",
            color: "#FFFFFF"
          },
          sort: "asc",
          autoHeight: true,
          lockPosition: "left",
          valueFormatter: valueFormatter,
          cellRenderer: DocumentNameColumn,
          cellRendererParams: {
            userEditAccessLevel: props.userEditAccessLevel,

            openFileVersionAdmin: function (documentId, fileName) {
              props.fileVersionAdmin.openFileVersionAdmin(documentId, fileName);
            }
          },
        };

      return columnDef;
    }

    /*
     * Date comparator for ag-grid-vue
     */
    function dateComparator(date1, date2) {
      if (date1 !== "" && date2 !== "") {
        const dateX = moment(date1, 'MM-DD-YYYY').toDate();
        const dateY = moment(date2, 'MM-DD-YYYY').toDate();

        return dateX > dateY ? 1 : dateX < dateY ? -1 : 0;
      } else if (date1 === "" && date2 !== "") {
        return -1;
      } else if (date1 !== "" && date2 === "") {
        return 1;
      } else {
        return 0;
      }
    }

    function setEvenColumnWidths() {
      const tableElement = document.querySelector(".ag-center-cols-viewport");

      if (tableElement) {
        const gridWidth = tableElement.offsetWidth;
  
        const displayedColumns = resultsGrid.value.api.getAllDisplayedColumns();
        const individualColumnWidth = (gridWidth - fileInfoColumnWidth) / (displayedColumns.length - 1);
  
        const columnsToBeSet = [];
        displayedColumns.forEach((column) => {
          if (column.colId !== "file_info") {
            columnsToBeSet.push({key: column.colId, newWidth: individualColumnWidth});
          }
        });
        
        resultsGrid.value.api.setColumnWidths(columnsToBeSet);
      }
    }

    function reSize() {
      setTimeout(() => {
        setEvenColumnWidths();
      }, 100);

      setTimeout(() => {
        setEvenColumnWidths();
      }, 300);
    }

    watch([CommonHelper.loadingQuerySearch, CommonHelper.loadingInitialSearchResults], () => {
      showHideOverlays();
    });

    function showHideOverlays() {
      if (CommonHelper.loadingQuerySearch.value || CommonHelper.loadingInitialSearchResults.value) {
        resultsGrid.value.api.setGridOption("loading", true);
      } else {
        if (CommonHelper.rowData.value.length > 0) {
          resultsGrid.value.api.setGridOption("loading", false);
          resultsGrid.value.api.hideOverlay();
        } else {
          resultsGrid.value.api.setGridOption("loading", false);
          resultsGrid.value.api.showNoRowsOverlay();
        }
      }
    }

    return {
      //variable properties
      resultsGrid,
      gridOptions,
      overlayLoadingTemplate,
      overlayNoRowsTemplate,
      defaultColDef,
      columnDefs,

      //functions
      reSize
    };
  }
};
</script>

<style lang="scss">
@use "~ag-grid-community/styles" as ag;

@include ag.grid-styles((
  theme: material
));

.ag-theme-material {
  --ag-borders: "false";
  --ag-borders-critical: "false";
  --ag-borders-secondary: "false";
  --ag-header-column-resize-handle-display: block;
  --ag-header-column-resize-handle-height: 50%;
  --ag-header-column-resize-handle-width: 2px;
  --ag-header-column-resize-handle-color: rgba(226, 226, 226, 0.5);
}

.ag-theme-material .ag-header {
  border: none;
}

.ag-theme-material .ag-header-row:not(:first-child) .ag-header-cell {
  border: none;
}

.ag-body {
  overflow-y: auto;
}

.ag-theme-material .ag-icon-first::before {
  color: var(--results-pageinate-color);
}

.ag-theme-material .ag-icon-previous::before {
  color: var(--results-pageinate-color);
}

.ag-theme-material .ag-icon-next::before {
  color: var(--results-pageinate-color);
}

.ag-theme-material .ag-icon-last::before {
  color: var(--results-pageinate-color);
}

.ag-root-wrapper {
  border-radius: 10px;
}

.ag-header-cell-label {
  font-size: 16px;
}

div[class~="ag-header-cell"] {
  color: black;
}

div[aria-colindex="1"][class="ag-cell-wrapper"] {
  padding: 5px 5px;
}

div[aria-rowindex="2"][class~="ag-header-row"] {
  height: 57px !important;
}

div[aria-colindex="1"][class~="ag-header-cell"][class~="ag-focus-managed"] {
  background-color: #0067c1;
  padding-left: 10px;
}

div[aria-colindex="2"][class~="ag-header-cell"][class~="ag-focus-managed"] {
  background-color: #0067c1;
  color: white;
}

div[class~="ag-header-cell"][class~="ag-focus-managed"] {
  background-color: var(--results-tableheaderbackground);
  color: var(--results-tableheadercolor);
}

div[aria-colindex="1"][class~="ag-header-cell"][class~="ag-focus-managed"][class~="ag-header-active"] {
  background-color: #0067C1 !important;
}

div[aria-colindex="2"][class~="ag-header-cell"][class~="ag-focus-managed"][class~="ag-header-active"] {
  background-color: #0067C1 !important;
  color: white !important;
}

div[class~="ag-header-cell"][class~="ag-focus-managed"][class~="ag-header-active"] {
  background-color: var(--results-tableheaderbackground) !important;
  color: var(--results-tableheadercolor) !important;
}

div[aria-colindex="1"][class~="ag-header-cell"][class~="ag-floating-filter"][class~="ag-focus-managed"] {
  background-color: #0067C1 !important;
}

div[aria-colindex="2"][class~="ag-header-cell"][class~="ag-floating-filter"][class~="ag-focus-managed"] {
  background-color: #0067C1 !important;
}

div[class~="ag-header-cell"][class~="ag-floating-filter"][class~="ag-focus-managed"] {
  background-color: var(--results-tableheaderbackground) !important;
  color: var(--results-tableheadercolor) !important;
}

div[aria-colindex="2"][class~="ag-header-cell"][class~="ag-focus-managed"] input[class^="ag-"][type="text"] {
  color: white;
}

.hidden-column-checkbox {
  padding-right: 1px;

  .q-checkbox__truthy {
    stroke: black;
  }
}
</style>
